import useVehicleOffersEventDescriptions from './react-query/use-vehicle-offers-event-descriptions/use-vehicle-offers-event-descriptions';
import { useTypedSelector } from './use-typed-selector';
import { AppPage, ShowInAppValues, User } from '../common/interfaces';
import { useAllAppPages } from '../components/order-status-section/use-all-app-pages';
import { useLocation } from 'react-router-dom';
import { detailsPagesMapping } from '../components/product/product-view';
import React from 'react';
import { USER_ROLES, VEHICLE_OFFER_EVENT_DESCRIPTIONS } from '../constants';
import OrderStatus from '../components/order-status-section/order-status';
import { takeStatusSectionLatestEvent, takeStatusSectionOldestEvent } from '../components/order-status-section/utils';
import { EventProps } from '../components/product/product-view.props';
import { VehicleOffersEventDescription } from './react-query/use-vehicle-offers-event-descriptions/use-vehicle-offers-event-descriptions.types';

const getStatusSectionEventDescriptions = (
  eventDescriptions: VehicleOffersEventDescription[],
  events: EventProps[],
  mainRoleId: string,
  appPage: AppPage | undefined,
  onlyDoneEvents?: boolean
) => {
  const eventDescriptionsWithHierarchy =
    eventDescriptions.filter(eventDescription => eventDescription.uds_hierarchie) || [];

  return eventDescriptionsWithHierarchy
    .filter(eventDescription => {
      const webrole = eventDescription.webroles.find(
        webrole => webrole.uds_apppageid?.id === appPage?.uds_appageid && webrole.uds_b2c_webrollid?.id === mainRoleId
      );
      if (!webrole) {
        return false;
      }
      const isDone = events.some(
        event => event.uds_eventdescription?.id === eventDescription.uds_fahrzeugbestellungeneventdescriptionid
      );
      if (onlyDoneEvents) {
        return isDone;
      }
      return (
        webrole.uds_show_in_appcode?.value === ShowInAppValues.Always ||
        (webrole.uds_show_in_appcode?.value === ShowInAppValues.OnlyIfDone && isDone)
      );
    })
    .map(eventDescription => ({
      ...eventDescription,
      eventsCount: events.filter(
        event => event.uds_eventdescription?.id === eventDescription.uds_fahrzeugbestellungeneventdescriptionid
      ).length
    }))
    .sort((a, b) => b.uds_hierarchie! - a.uds_hierarchie!);
};

const getOrderStatus = (
  events: EventProps[],
  eventDescription: VehicleOffersEventDescription,
  mainRoleId: string,
  statusesContainerWidth: number,
  appPage?: AppPage
) => {
  if (
    eventDescription.eventsCount &&
    eventDescription.eventsCount > 1 &&
    ![
      VEHICLE_OFFER_EVENT_DESCRIPTIONS.configurationCreated.id,
      VEHICLE_OFFER_EVENT_DESCRIPTIONS.configurationUploaded.id,
      VEHICLE_OFFER_EVENT_DESCRIPTIONS.vehicleConfigurationBasedOnGRVCreated.id
    ].includes(eventDescription.uds_fahrzeugbestellungeneventdescriptionid)
  ) {
    return Array(eventDescription.eventsCount)
      .fill(null)
      .map((_, i) => (
        <OrderStatus
          key={eventDescription.uds_fahrzeugbestellungeneventdescriptionid + `_${i}`}
          eventDescription={eventDescription}
          event={takeStatusSectionLatestEvent(
            events.filter(
              event => event.uds_eventdescription?.id === eventDescription.uds_fahrzeugbestellungeneventdescriptionid
            )
          )}
          mainRoleId={mainRoleId}
          appPage={appPage}
          statusesContainerWidth={statusesContainerWidth}
        />
      ));
  }
  return (
    <OrderStatus
      key={eventDescription.uds_fahrzeugbestellungeneventdescriptionid}
      eventDescription={eventDescription}
      event={takeStatusSectionOldestEvent(
        events.filter(
          event => event.uds_eventdescription?.id === eventDescription.uds_fahrzeugbestellungeneventdescriptionid
        )
      )}
      mainRoleId={mainRoleId}
      appPage={appPage}
      statusesContainerWidth={statusesContainerWidth}
    />
  );
};

const useEventDescriptionsUtils = () => {
  const location = useLocation();
  const user = useTypedSelector<User>(state => state.app.user);
  const { vehicleOffersEventDescriptions } = useVehicleOffersEventDescriptions();
  const { appPages } = useAllAppPages();
  const pageUrl = location.pathname.replace(/\/$/, '') as keyof typeof detailsPagesMapping;
  const mainRoleId = user.mainRole?.id === USER_ROLES.administrator.id ? USER_ROLES.fleetManager.id : user.mainRole.id;

  const getAppStatusTextById = (eventDescriptionId: string): { appStatusText: string; isDone: boolean } => {
    const appPage = appPages?.find(appPage => appPage.uds_page_url === detailsPagesMapping[pageUrl]);
    const eventDescription = vehicleOffersEventDescriptions?.find(
      item => item.uds_fahrzeugbestellungeneventdescriptionid === eventDescriptionId
    );
    const currentEventDescriptionWebRole = eventDescription?.webroles.find(
      webrole => webrole.uds_apppageid?.id === appPage?.uds_appageid && webrole.uds_b2c_webrollid?.id === mainRoleId
    );

    return {
      appStatusText:
        currentEventDescriptionWebRole?.uds_app_status_text_when_done ||
        currentEventDescriptionWebRole?.uds_app_status_text ||
        '',
      isDone: !!currentEventDescriptionWebRole?.uds_app_status_text_when_done
    };
  };

  const getEventDescriptionForVehicleStatus = (events: EventProps[]): VehicleOffersEventDescription | undefined => {
    const appPage = appPages?.find(appPage => appPage.uds_page_url === detailsPagesMapping[pageUrl]);
    const eventDescriptions = getStatusSectionEventDescriptions(
      vehicleOffersEventDescriptions || [],
      events,
      mainRoleId,
      appPage
    );

    return eventDescriptions.filter(eventDescription =>
      events.some(
        event => event.uds_eventdescription?.id === eventDescription.uds_fahrzeugbestellungeneventdescriptionid
      )
    )[0];
  };

  const getEventDescriptionsForStatusSection = (
    events: EventProps[],
    showOnlyDoneEvents: boolean,
    statusesContainerWidth: number
  ) => {
    const appPage = appPages?.find(appPage => appPage.uds_page_url === pageUrl);
    return getStatusSectionEventDescriptions(
      vehicleOffersEventDescriptions || [],
      events,
      mainRoleId,
      appPage,
      showOnlyDoneEvents
    ).flatMap(eventDescription => {
      return getOrderStatus(events, eventDescription, mainRoleId, statusesContainerWidth, appPage);
    });
  };

  return { getAppStatusTextById, getEventDescriptionForVehicleStatus, getEventDescriptionsForStatusSection };
};

export default useEventDescriptionsUtils;
